import type { IDrag, IDraggedItem } from "./DragAndDropContext";
import { t } from "@lingui/core/macro";
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useDragAndDropContext } from "./DragAndDropContext";
import { useSelection } from "../Selection/SelectionContext";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { getPatientFullName } from "@medoma/models/patients";
import { isGroup, isRoutable } from "@medoma/models/activities";
import { shiftName, type IScheduledShift } from "@medoma/models/shifts";

// A visit is dragged out of a route and so is routable by definition; it carries no
// category of its own. Anything else is routable iff its category is.
export const isDraggedItemRoutable = (drag: IDrag | undefined) =>
  !!drag && (drag.source === "route" || isRoutable(drag.item.category));

// A routable item dropped on a shift starts a new route there; anything else is
// added to the shift as it is.
export const getShiftDropZoneLabel = (
  drag: IDrag | undefined,
  shift: Pick<IScheduledShift, "employee" | "competence">,
): string => {
  const nameOfShift = shiftName(shift, {
    competenceFormat: "full",
    employeeNameFormat: "full",
  });
  return isDraggedItemRoutable(drag)
    ? t`Skapa rutt för ${nameOfShift}`
    : t`Lägg till aktivitet för ${nameOfShift}`;
};

export const getItemLabel = (item: IDraggedItem): string => {
  // Patient name if it exists
  if (item.patient && "name" in item.patient) {
    return getPatientFullName(item.patient.name);
  }

  // Generic label for groups without a patient name (should not be possible,
  // but keep the drag label useful if it happens).
  if (isGroup(item)) {
    return t`Besök`;
  }

  // Activity title for activity occurrences without patient name
  if ("title" in item) {
    return item.title;
  }

  // If everything else fails, return empty string
  return "";
};

// For drag and drop mutations, we want a consistent behavior for all mutations, so we use this wrapper.
export const useDragAndDropMutation = <
  TData = unknown,
  TVariables = void,
  TError = Error,
>({
  mutationFn,
  errorMessage,
}: {
  mutationFn: (variables: TVariables) => Promise<TData>;
  errorMessage: string;
}): UseMutationResult<TData, TError, TVariables> => {
  const queryClient = useQueryClient();
  const { clearSelection } = useSelection();
  const { endDrag } = useDragAndDropContext();
  const { displayErrorMessageAlert } = useErrorMessageAlert();

  return useMutation<TData, TError, TVariables>({
    mutationFn,
    onMutate: () => {
      clearSelection();
    },
    onError: (err) => {
      displayErrorMessageAlert(`${errorMessage} ${deducedError(err)}`);
    },
    onSuccess: () => {
      // Don't await this, it will block the UI
      // We accept that the UI is not updated immediately for a snappier experience
      void queryClient.invalidateQueries();
    },
    onSettled: () => {
      endDrag();
    },
  });
};
